import React, { useState, useEffect, useMemo } from 'react';
import DeleteConfirmation from '../DeleteConfirmation/DeleteConfirmation';
import NotVioModal from '../NotVioModal/NotVioModal';
import ReportedNews from '../ReportedNews/ReportedNews';
import moment from 'moment';
import { capitalizeFirstLetter } from '../../../helpers/utilities';
import { getUserDisplayName } from '../../../helpers/userDisplayName';
import { fetchAdminDeleteCreation, fetchMarkSafe } from '../../Reducers/actions/creationsActions';
import { fetchApi } from '../../../helpers/fetch';

const ManageContent = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);
    const [toRemove, setToRemove] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [isDeleting, setIsDeleting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('creations');
    const rowsPerPage = 10;

    useEffect(() => {
        const fetchReports = async () => {
            try {
                setLoading(true);
                const response = await fetchApi('/admin/reported-creations');
                if (response.success) {
                    setReports(response.data || []);
                } else {
                    setError('Failed to fetch reports');
                }
            } catch (err) {
                setError('Error fetching reports');
            } finally {
                setLoading(false);
            }
        };

        fetchReports();
    }, []);

    const sortedData = useMemo(() => {
        let filteredReports = reports;
        
        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filteredReports = reports.filter(report => matchesSearchQuery(report, query));
        }
        
        // If no sorting is applied, return filtered data as is
        if (!sortConfig.key) {
            return filteredReports;
        }
        
        // Create a new array to avoid mutating the original
        const sortedReports = [...filteredReports].sort((a, b) => {
            const getSortValue = (report, key) => {
                switch (key) {
                    case "created_at":
                        return new Date(report[key]);
                    case "report_id":
                    case "reporter_id":
                    case "creation_id":
                        return parseInt(report[key]) || 0;
                    case "creation_title":
                        return (report.creation?.title || '').toString().toLowerCase();
                    case "reporter_name":
                        return `${report.reporter?.firstName || ''} ${report.reporter?.lastName || ''}`.trim().toLowerCase();
                    default:
                        return (report[key] || '').toString().toLowerCase();
                }
            };

            const aValue = getSortValue(a, sortConfig.key);
            const bValue = getSortValue(b, sortConfig.key);

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        
        return sortedReports;
    }, [reports, sortConfig, searchQuery]);

    const paginatedData = useMemo(() => {
        return sortedData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
    }, [sortedData, currentPage]);

    const totalPages = useMemo(() => {
        return Math.ceil(sortedData.length / rowsPerPage);
    }, [sortedData.length]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'rejected': return 'fg-red';
            case 'accepted': return 'fg-green';
            default: return 'fg-amber';
        }
    };

    const renderStatus = (status) => (
        <span className={`status-toggle ${status}`}>
            {capitalizeFirstLetter(status.replace('_', ' '))}
        </span>
    );

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return '';
        return sortConfig.direction === 'asc' ? '↑' : '↓';
    };

    const renderActionButtons = (creation) => (
        <div className="action-buttons">
            <button 
                className="delete-btn" 
                onClick={() => setToRemove(creation)}
                disabled={isDeleting}
            >
                {isDeleting && toRemove?.creation_id === creation.creation_id ? 'Deleting...' : 'Delete Creation'}
            </button>
            <button 
                className="edit-btn" 
                onClick={() => setSelected(creation)} 
                disabled={creation.status !== 'under review' || isDeleting}
            >
                Mark as Not Violating
            </button>
        </div>
    );

    const handleRemoveCreation = async () => {
        try {
            setIsDeleting(true);
            const response = await fetchAdminDeleteCreation(toRemove.creation_id);
            
            if (response.success) {
                // Remove all reports for this creation since the creation is now deleted
                setReports(prevReports => 
                    prevReports.filter(report => report.creation_id !== toRemove.creation_id)
                );
            }
        } catch (error) {
            // Silent error handling - user can see the creation wasn't deleted
        } finally {
            setIsDeleting(false);
            setToRemove(null);
        }
    };

    const handleNotVio = async () => {
        try {
            const response = await fetchMarkSafe(selected.creation_id);
            if (response.success) {
                setReports(prevReports => 
                    prevReports.map(report => 
                        report.creation_id === selected.creation_id 
                            ? { ...report, status: 'rejected' }
                            : report
                    )
                );
            }
        } catch (error) {
            // Silent error handling - user can see the status wasn't updated
        }
        setSelected(null);
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
        setCurrentPage(1); // Reset to first page when searching
    };

    const matchesSearchQuery = (report, query) => {
        return (
            report.report_id?.toString().includes(query) ||
            report.reporter_id?.toString().includes(query) ||
            report.creation_id?.toString().includes(query) ||
            report.reason?.toLowerCase().includes(query) ||
            report.status?.toLowerCase().includes(query) ||
            moment(report.created_at).format("DD-MM-YYYY").includes(query) ||
            // New fields from backend
            report.creation?.title?.toLowerCase().includes(query) ||
            report.reporter?.firstName?.toLowerCase().includes(query) ||
            report.reporter?.lastName?.toLowerCase().includes(query) ||
            report.reporter?.userName?.toLowerCase().includes(query)
        );
    };

    if (loading) {
        return <div className="hyk-table">Loading...</div>;
    }

    if (error) {
        return <div className="hyk-table">Error: {error}</div>;
    }

    if (!reports || reports.length === 0) {
        return <div className="hyk-table">No reported creations found.</div>;
    }

    return (
        <div className="hyk-table">
            <div className="content-tabs">
                <button 
                    className={`tab-button ${activeTab === 'creations' ? 'active' : ''}`}
                    onClick={() => setActiveTab('creations')}
                >
                    Reported Creations
                </button>
                <button 
                    className={`tab-button ${activeTab === 'news' ? 'active' : ''}`}
                    onClick={() => setActiveTab('news')}
                >
                    Reported News
                </button>
            </div>

            {activeTab === 'news' ? (
                <ReportedNews />
            ) : (
                <>
            <div className="hyk-table">
            </div>
            <div className="table-wrapper">
            <table>
                <thead>
                    <tr>
                        <th onClick={() => handleSort("report_id")} className='c-pointer sortable-header'>
                            Report ID {getSortIcon("report_id")}
                        </th>
                        <th onClick={() => handleSort("created_at")} className='c-pointer sortable-header'>
                            Reported Date {getSortIcon("created_at")}
                        </th>
                        <th onClick={() => handleSort("reporter_name")} className='c-pointer sortable-header'>
                            Reporter {getSortIcon("reporter_name")}
                        </th>
                        <th onClick={() => handleSort("creation_title")} className='c-pointer sortable-header'>
                            Creation Title {getSortIcon("creation_title")}
                        </th>
                        <th onClick={() => handleSort("reason")} className='c-pointer sortable-header'>
                            Reason {getSortIcon("reason")}
                        </th>
                        <th onClick={() => handleSort("status")} className='c-pointer sortable-header'>
                            Status {getSortIcon("status")}
                        </th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {paginatedData.map(creation => (
                        <tr key={creation.report_id}>
                            <td>{creation.report_id}</td>
                            <td>{moment(creation.created_at).format("DD-MM-YYYY")}</td>
                            <td>
                                {creation.reporter ? 
                                    getUserDisplayName(creation.reporter) : 
                                    creation.reporter_id
                                }
                            </td>
                            <td>
                                {creation.creation ? 
                                    <a 
                                        href={`/creations/${creation.creation_id}`} 
                                        className="creation-link"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {creation.creation.title}
                                    </a> : 
                                    creation.creation_id
                                }
                            </td>
                            <td>{creation.reason}</td>
                            <td>{renderStatus(creation.status)}</td>
                            <td>{renderActionButtons(creation)}</td>
                        </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="pagination">
                <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    Previous
                </button>

                <span>Page {currentPage} of {totalPages}</span>

                <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    Next
                </button>
            </div>

            {selected && (
                <NotVioModal
                    item_name={selected.creatorName}
                    onClose={() => setSelected(null)}
                    onConfirm={handleNotVio}
                />
            )}

            {toRemove && (
                <DeleteConfirmation
                    item_name={`creation (ID: ${toRemove.creation_id})`}
                    item_type="creation"
                    onClose={() => !isDeleting && setToRemove(null)}
                    onConfirm={handleRemoveCreation}
                    isLoading={isDeleting}
                />
            )}
                </>
            )}
        </div>
    );
};

export default ManageContent;
